const AUTH_RECOVERY_STATE_KEY = 'omniwound.auth.recovery.state';

export type AuthRecoveryState = 'idle' | 'redirecting' | 'verifying';

function storage(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.sessionStorage;
}

export function getAuthRecoveryState(): AuthRecoveryState {
  const state = storage()?.getItem(AUTH_RECOVERY_STATE_KEY);
  return state === 'redirecting' || state === 'verifying' ? state : 'idle';
}

export function markAuthRedirectStarted(): boolean {
  const store = storage();
  if (!store) {
    return true;
  }

  if (getAuthRecoveryState() !== 'idle') {
    return false;
  }

  store.setItem(AUTH_RECOVERY_STATE_KEY, 'redirecting');
  return true;
}

/**
 * A `redirecting` marker can only have reached a newly loaded document through
 * the OAuth round-trip. Move it to `verifying` before children issue their BFF
 * queries; persistent 401s in this state are intentionally not redirected.
 */
export function markAuthRecoveryDocumentVerifying(): void {
  if (getAuthRecoveryState() === 'redirecting') {
    storage()?.setItem(AUTH_RECOVERY_STATE_KEY, 'verifying');
  }
}

/**
 * Completes recovery only after the post-redirect document observes a real BFF
 * success. A successful token lookup or pre-navigation request is insufficient.
 */
export function completeAuthRecoveryAfterBffSuccess(): void {
  if (getAuthRecoveryState() === 'verifying') {
    storage()?.removeItem(AUTH_RECOVERY_STATE_KEY);
  }
}

/** Clear a marker created for a probe that proved no redirect should occur. */
export function cancelTentativeAuthRedirect(): void {
  if (getAuthRecoveryState() === 'redirecting') {
    storage()?.removeItem(AUTH_RECOVERY_STATE_KEY);
  }
}

/**
 * An authenticated principal routed to Account Access or support cannot mount
 * the API provider that normally completes post-login verification. Clear
 * either recovery marker so a later repaired principal can recover from a
 * genuine 401 in the same browser session.
 */
export function resetAuthRecoveryForPrincipalFault(): void {
  storage()?.removeItem(AUTH_RECOVERY_STATE_KEY);
}
